import {
  Outlet,
  Link,
  Navigate,
  createRootRouteWithContext,
  useRouterState,
} from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { CircleUserRound } from "lucide-react";
import { useSession } from "../lib/auth.js";
import { SyncBanner } from "../components/SyncBanner.js";
import { SyncErrorToasts } from "../components/SyncErrorToasts.js";
import { SyncToasts } from "../components/SyncToasts.js";
import { Brand } from "../components/Brand.js";

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  component: RootLayout,
});

// Atteignables sans session : `/reset` porte son propre écran « lien expiré »,
// `/login` est la destination du garde lui-même.
const PUBLIC_PATHS = ["/login", "/reset"];

function RootLayout() {
  const session = useSession();
  // `href` = pathname + search + hash : l'URL complète à rejouer après
  // reconnexion, paramètres de tracking des mails compris.
  const href = useRouterState({ select: (s) => s.location.href });
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Garde unique de l'app. Sans elle, un lien de mission ouvert avec une
  // session morte laissait `/tasks/$taskId` sur « Chargement… » indéfiniment :
  // le service offline n'est monté que pour une session authentifiée, donc le
  // cache ne répondait jamais — et l'URL demandée était perdue.
  const onPublicPath = PUBLIC_PATHS.includes(pathname.replace(/\/$/, "") || "/");
  const needsLogin = session.status === "anon" && !onPublicPath;

  return (
    <div className="min-h-screen bg-background text-foreground font-sans flex flex-col">
      {session.status === "authed" && <SyncBanner />}
      <header className="border-b px-4 py-3 flex items-center justify-between">
        <Link to="/" aria-label="Greenkub /relais — accueil">
          <Brand />
        </Link>
        {session.status === "authed" && (
          <div className="flex items-center gap-4">
            <Link
              to="/profil"
              className="flex items-center gap-1.5 text-sm text-muted-foreground"
            >
              <CircleUserRound className="size-5" aria-hidden />
              Profil
            </Link>
          </div>
        )}
      </header>
      <main className="p-4 flex-1 max-w-2xl mx-auto w-full flex flex-col min-h-0">
        {needsLogin ? (
          <Navigate to="/login" search={{ redirect: href }} replace />
        ) : session.status === "loading" && !onPublicPath ? (
          <p className="text-sm text-muted-foreground">…</p>
        ) : (
          <Outlet />
        )}
      </main>
      {session.status === "authed" && <SyncErrorToasts />}
      {session.status === "authed" && <SyncToasts />}
    </div>
  );
}
